import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';

import Swal from 'sweetalert2';
import {
  Pencil,
  Trash2,
  Plus,
  ChevronRight
} from 'lucide-react';

function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    sku: '',
    price: '',
    priceMin: '',
    priceMax: '',
    description: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setProducts(response.data.products);
      setLoading(false);
    } catch (err) {
      console.error('Fetch products error:', err);
      setError('Failed to load products');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const parsedPrice = parseFloat(formData.price);
      if (Number.isNaN(parsedPrice)) {
        alert('Please enter a valid price.');
        return;
      }
      const parsedMin = formData.priceMin !== '' ? parseFloat(formData.priceMin) : null;
      const parsedMax = formData.priceMax !== '' ? parseFloat(formData.priceMax) : null;
      if (parsedMin !== null && Number.isNaN(parsedMin)) {
        alert('Please enter a valid minimum price or leave it blank.');
        return;
      }
      if (parsedMax !== null && Number.isNaN(parsedMax)) {
        alert('Please enter a valid maximum price or leave it blank.');
        return;
      }
      if (parsedMin !== null && parsedMax !== null && parsedMin > parsedMax) {
        alert('Minimum price cannot be greater than maximum price.');
        return;
      }

      const payload = {
        name: formData.name,
        brand: formData.brand,
        sku: formData.sku,
        price: parsedPrice,
        price_min: parsedMin,
        price_max: parsedMax,
        description: formData.description
      };

      if (editingProduct) {
        // Update product
        await api.put(`/admin/products/${editingProduct.id}`, payload);
      } else {
        // Create product
        await api.post('/admin/products', payload);
      }
      
      // Reset form and refresh list
      setFormData({ name: '', brand: '', sku: '', price: '', priceMin: '', priceMax: '', description: '' });
      setShowAddForm(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (err) {
      console.error('Save product error:', err);
      alert('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      brand: product.brand || '',
      sku: product.sku || '',
      price: product.price.toString(),
      priceMin: product.price_min !== null && product.price_min !== undefined ? product.price_min.toString() : '',
      priceMax: product.price_max !== null && product.price_max !== undefined ? product.price_max.toString() : '',
      description: product.description || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id, forceDelete = false) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: forceDelete 
        ? "This will remove the product from all campaigns and entries. This action cannot be undone!"
        : "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: forceDelete ? 'Yes, force delete!' : 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/products/${id}${forceDelete ? '?force=true' : ''}`);
        setSuccessMessage('Your product has been deleted.');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
        fetchProducts();
      } catch (err) {
        console.error('Delete product error:', err);
        
        // Check if it's a reference error and offer force delete
        if (err.response?.data?.hasReferences) {
          const forceResult = await Swal.fire({
            title: 'Product has references',
            html: err.response.data.error + '<br><br>Do you want to force delete and remove all references?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Force Delete',
            cancelButtonText: 'Cancel'
          });
          
          if (forceResult.isConfirmed) {
            handleDelete(id, true);
          }
        } else {
          const errorMessage = err.response?.data?.error || 'Failed to delete product.';
          Swal.fire(
            'Error!',
            errorMessage,
            'error'
          );
        }
      }
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', brand: '', sku: '', price: '', priceMin: '', priceMax: '', description: '' });
    setShowAddForm(false);
    setEditingProduct(null);
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const handleBulkDelete = async (forceDelete = false) => {
    if (selectedProducts.length === 0) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: forceDelete 
        ? `This will remove ${selectedProducts.length} products from all campaigns and entries. This action cannot be undone!`
        : `You are about to delete ${selectedProducts.length} products. You won't be able to revert this!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: forceDelete ? 'Yes, force delete all!' : 'Yes, delete all!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.post('/admin/products/bulk-delete', {
          productIds: selectedProducts,
          force: forceDelete
        });
        await Swal.fire(
          'Deleted!',
          `${selectedProducts.length} products have been deleted.`,
          'success'
        );
        setSelectedProducts([]);
        fetchProducts();
      } catch (err) {
        console.error('Bulk delete error:', err);
        
        // Check if it's a reference error and offer force delete
        if (err.response?.data?.hasReferences) {
          const forceResult = await Swal.fire({
            title: 'Some products have references',
            html: err.response.data.error + '<br><br>Do you want to force delete and remove all references?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Force Delete All',
            cancelButtonText: 'Cancel'
          });
          
          if (forceResult.isConfirmed) {
            handleBulkDelete(true);
          }
        } else {
          const errorMessage = err.response?.data?.error || 'Failed to delete products.';
          Swal.fire(
            'Error!',
            errorMessage,
            'error'
          );
        }
      }
    }
  };

  const formatPriceRange = (min, max) => {
    const hasMin = min !== null && min !== undefined && min !== '';
    const hasMax = max !== null && max !== undefined && max !== '';
    if (!hasMin && !hasMax) return '-';
    const toCurrency = (value) => `$${parseFloat(value).toFixed(2)}`;
    if (hasMin && hasMax) {
      return `${toCurrency(min)} - ${toCurrency(max)}`;
    }
    if (hasMin) {
      return `≥ ${toCurrency(min)}`;
    }
    return `≤ ${toCurrency(max)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading products...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <AdminLayout activeSection="PRODUCTS">
      <style>
        {`
          @keyframes fadeInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .animate-fadeInRight {
            animation: fadeInRight 0.3s ease-out;
          }
        `}
      </style>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error notification */}
      {showError && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{errorMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <h1 className="text-4xl font-bold text-white mb-6">PRODUCTS</h1>

      {/* Add/Edit Product Dialog */}
      {showAddForm && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && handleCancel()}
        >
          <div className="relative bg-white rounded-3xl shadow-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Product Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter product name"
                  />
                </div>
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Brand
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter brand (optional)"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    SKU
                  </label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter SKU (optional)"
                  />
                </div>
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    required
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Min Receipt Price (optional)
                  </label>
                  <input
                    type="number"
                    name="priceMin"
                    step="0.01"
                    min="0"
                    value={formData.priceMin}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Max Receipt Price (optional)
                  </label>
                  <input
                    type="number"
                    name="priceMax"
                    step="0.01"
                    min="0"
                    value={formData.priceMax}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Description
                  </label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Enter description (optional)"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors"
                >
                  {editingProduct ? 'Update' : 'Add'} Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Products Table */}
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
        {/* Bulk Actions Bar */}
        {selectedProducts.length > 0 && (
          <div className="bg-gray-100 px-6 py-4 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => handleBulkDelete()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              <Trash2 className="w-4 h-4 inline-block mr-1" />
              Delete Selected
            </button>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider w-16">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && selectedProducts.length === products.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Product Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Brand
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Receipt Price Range
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-sm text-gray-500">
                    No products found. Add your first product above.
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleSelectProduct(product.id)}
                        className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {product.brand || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {product.sku || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${parseFloat(product.price).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatPriceRange(product.price_min, product.price_max)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {product.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-teal-600 hover:text-teal-800"
                          title="Edit"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {products.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No products yet. Add your first product!</p>
            </div>
          )}
        </div>

        {/* Footer with Add Button */}
        <div className="px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => setShowAddForm(true)}
            className="w-12 h-12 bg-teal-800 text-white rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors shadow-lg"
            title="Add New Product"
          >
            <Plus className="w-8 h-8" />
          </button>

          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">1/1</span>
            <button className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default Products;
